/**
 * Work-queue (action item) schemas, derived from the generated Zod.
 *
 * The wire source is the `work-queue` endpoint, not anything named
 * `action-item`: `WorkQueueListResponse.results` IS the action item.
 *
 * See ./README.md.
 */
import { z } from 'zod';

import {
  WorkQueueListQueryParams,
  WorkQueueListResponse,
} from '~/data/generated/zod/work-queue/work-queue.zod';

const generatedActionItem = WorkQueueListResponse.shape.results.element;

/**
 * One item in the work queue.
 *
 * `severity`, `description` and `valueLabel` are `.optional()` on the generated
 * base only because their Django model fields carry defaults, which makes
 * drf-spectacular mark them non-required — every construction path
 * (`ActionItemPayload` in `main/action_items/registry.py`) supplies a value
 * (AMP-620). The defaults below keep the shape the UI reads without claiming
 * the key is guaranteed on the wire.
 *
 * Everything else is inherited: `eventType`, `severity` and `suggestedAction`
 * are the generated enums (the empty-string member of `suggestedAction` means
 * "the AI made no recommendation"), `property`/`portfolio` are null for
 * AI-pipeline items, and `target` says which detail drawer the row opens.
 */
export const actionItemSchema = generatedActionItem.extend({
  severity: generatedActionItem.shape.severity.default('warning'),
  description: generatedActionItem.shape.description.default(''),
  valueLabel: generatedActionItem.shape.valueLabel.default(''),
  // Decimal string, not coerced: every consumer formats or truthiness-tests it.
  valueAtStake: generatedActionItem.shape.valueAtStake.default(null),
  // Set and in the future ⇒ snoozed. The snooze lives on the item and is shared
  // across every consultant who sees it.
  snoozedUntil: generatedActionItem.shape.snoozedUntil.default(null),
});
export type ActionItem = z.infer<typeof actionItemSchema>;

export type ActionItemEventType = ActionItem['eventType'];
export type ActionItemSeverity = ActionItem['severity'];
export type ActionItemSuggestedAction = ActionItem['suggestedAction'];

/**
 * `count`/`next`/`previous` keep their generated optionality: DRF only emits
 * them when the request was paginated (README §7).
 */
export const actionItemListResponseSchema = WorkQueueListResponse.extend({
  results: z.array(actionItemSchema),
});
export type ActionItemListResponse = z.infer<typeof actionItemListResponseSchema>;

type WorkQueueQuery = z.infer<typeof WorkQueueListQueryParams>;

/**
 * The app-side filter for the work-queue read. Each member is the generated
 * query param's own type; only the names differ, and `useActionItems` maps them
 * back to snake_case at the fetch boundary (AMP-503).
 */
export type ActionItemFilterParams = {
  eventType?: WorkQueueQuery['event_type'];
  severity?: WorkQueueQuery['severity'];
  /**
   * approve/reject/partial only. An item's empty-string `suggestedAction` is a
   * value ("no AI recommendation"), not a filterable choice.
   */
  suggestedAction?: WorkQueueQuery['suggested_action'];
  portfolio?: WorkQueueQuery['portfolio'];
  property?: WorkQueueQuery['property'];
  /** One elevator. Resolved server-side through each item's invoice, contract or certificate. */
  car?: WorkQueueQuery['car'];
  region?: WorkQueueQuery['region'];
  includeSnoozed?: WorkQueueQuery['include_snoozed'];
  page?: WorkQueueQuery['page'];
  pageSize?: WorkQueueQuery['page_size'];
};
